import { useEffect, useState } from 'react';
import { useForm, type Resolver } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { AlertCircle, Loader2, X } from 'lucide-react';
import {
  SERVICE_STATUTS,
  SERVICE_TYPES,
  createServiceSchema,
  type CreateServiceInput,
} from '@mygmao/shared';
import { api, extractErrorMessage } from '../../lib/api';
import { ModalPortal } from '../ModalPortal';

/**
 * Modal CRUD service (US-SVC-02). Champs Form Builder déférés.
 */

interface ParentOption {
  id: string;
  code: string;
  nom_complet: string;
}

export function ServiceFormModal({
  serviceId,
  defaultParentId,
  onClose,
  onSaved,
}: {
  serviceId?: string;
  defaultParentId?: string;
  onClose(): void;
  onSaved(): void;
}) {
  const isEdit = !!serviceId;
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [parents, setParents] = useState<ParentOption[]>([]);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<CreateServiceInput>({
    resolver: zodResolver(createServiceSchema) as unknown as Resolver<CreateServiceInput>,
    defaultValues: {
      type_service: 'operationnel',
      statut: 'actif',
      service_parent_id: defaultParentId ?? null,
      capacite_maximale_simultanee: 0,
      nombre_postes_travail: 0,
      autorisation_deplacement_equipement: true,
      validation_requise_deplacement: false,
    },
  });

  useEffect(() => {
    api
      .get<{ data: ParentOption[] }>('/services?root_only=false&page_size=200')
      .then((res) => setParents(res.data.data))
      .catch(() => {
        // Silencieux : l'utilisateur pourra créer sans parent.
      });
  }, []);

  useEffect(() => {
    if (!serviceId) {
      api
        .get<{ data: { code: string } }>('/numbering/suggest?kind=service')
        .then((res) => reset((prev) => ({ ...prev, code: res.data.data.code }) as typeof prev))
        .catch(() => {
          // Silencieux.
        });
      return;
    }
    (async () => {
      try {
        const res = await api.get<{ data: CreateServiceInput }>(`/services/${serviceId}`);
        reset({
          code: res.data.data.code,
          nom_complet: res.data.data.nom_complet,
          type_service: res.data.data.type_service,
          statut: res.data.data.statut,
          service_parent_id: res.data.data.service_parent_id ?? null,
          capacite_maximale_simultanee: res.data.data.capacite_maximale_simultanee ?? 0,
          nombre_postes_travail: res.data.data.nombre_postes_travail ?? 0,
          surface_totale: res.data.data.surface_totale ?? undefined,
          autorisation_deplacement_equipement: res.data.data.autorisation_deplacement_equipement,
          validation_requise_deplacement: res.data.data.validation_requise_deplacement,
        });
      } catch (err) {
        setErrorMessage(extractErrorMessage(err));
      } finally {
        setLoading(false);
      }
    })();
  }, [serviceId, reset]);

  async function onSubmit(values: CreateServiceInput) {
    setSaving(true);
    setErrorMessage(null);
    try {
      const payload = {
        ...values,
        service_parent_id: values.service_parent_id || null,
        capacite_maximale_simultanee: values.capacite_maximale_simultanee ?? 0,
        nombre_postes_travail: values.nombre_postes_travail ?? 0,
        surface_totale: values.surface_totale || null,
      };
      if (isEdit) {
        await api.patch(`/services/${serviceId}`, payload);
      } else {
        await api.post('/services', payload);
      }
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  async function handleDelete() {
    if (!serviceId) return;
    if (!confirm('Supprimer ce service ? (soft delete — uniquement si aucun enfant rattaché)'))
      return;
    setSaving(true);
    try {
      await api.delete(`/services/${serviceId}`);
      onSaved();
    } catch (err) {
      setErrorMessage(extractErrorMessage(err));
    } finally {
      setSaving(false);
    }
  }

  // Empêche le service de se choisir lui-même comme parent dans la liste.
  const parentOptions = parents.filter((p) => p.id !== serviceId);

  return (
    <ModalPortal>
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-xl max-w-2xl w-full shadow-2xl max-h-[90vh] flex flex-col">
          <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
            <h2 className="text-lg font-semibold text-gray-900">
              {isEdit ? 'Modifier le service' : 'Nouveau service'}
            </h2>
            <button
              onClick={onClose}
              className="p-1 rounded-lg hover:bg-gray-100"
              aria-label="Fermer"
            >
              <X className="w-5 h-5 text-gray-500" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {errorMessage && (
              <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 flex items-start space-x-2">
                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                <p className="text-sm text-red-700">{errorMessage}</p>
              </div>
            )}

            {loading ? (
              <div className="flex items-center justify-center p-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary-500" />
              </div>
            ) : (
              <form id="service-form" onSubmit={handleSubmit(onSubmit)} className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <Field label="Code *" error={errors.code?.message}>
                    <input
                      {...register('code')}
                      placeholder="SERV-001"
                      className={inputClass + ' uppercase'}
                    />
                  </Field>
                  <Field label="Nom complet *" error={errors.nom_complet?.message}>
                    <input {...register('nom_complet')} className={inputClass} />
                  </Field>
                  <Field label="Type *" error={errors.type_service?.message}>
                    <select {...register('type_service')} className={inputClass}>
                      {SERVICE_TYPES.map((t) => (
                        <option key={t} value={t}>
                          {t.replace(/_/g, ' ')}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Statut *" error={errors.statut?.message}>
                    <select {...register('statut')} className={inputClass}>
                      {SERVICE_STATUTS.map((s) => (
                        <option key={s} value={s}>
                          {s.replace(/_/g, ' ')}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field
                    label="Service parent"
                    error={errors.service_parent_id?.message}
                    className="md:col-span-2"
                  >
                    <select
                      {...register('service_parent_id', {
                        setValueAs: (v) => (v === '' ? null : v),
                      })}
                      className={inputClass}
                    >
                      <option value="">— Aucun (racine) —</option>
                      {parentOptions.map((p) => (
                        <option key={p.id} value={p.id}>
                          {p.code} — {p.nom_complet}
                        </option>
                      ))}
                    </select>
                  </Field>
                  <Field
                    label="Capacité maximale simultanée"
                    error={errors.capacite_maximale_simultanee?.message}
                  >
                    <input
                      type="number"
                      min="0"
                      {...register('capacite_maximale_simultanee', { valueAsNumber: true })}
                      className={inputClass}
                    />
                  </Field>
                  <Field
                    label="Nombre de postes de travail"
                    error={errors.nombre_postes_travail?.message}
                  >
                    <input
                      type="number"
                      min="0"
                      {...register('nombre_postes_travail', { valueAsNumber: true })}
                      className={inputClass}
                    />
                  </Field>
                  <Field
                    label="Surface totale (m²)"
                    error={errors.surface_totale?.message}
                    className="md:col-span-2"
                  >
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      {...register('surface_totale', {
                        setValueAs: (v) => (v === '' || v === null ? null : Number(v)),
                      })}
                      className={inputClass}
                    />
                  </Field>
                  <label className="flex items-center space-x-2 text-sm text-gray-700 md:col-span-2">
                    <input
                      type="checkbox"
                      {...register('autorisation_deplacement_equipement')}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>Autorisation déplacement d'équipement</span>
                  </label>
                  <label className="flex items-center space-x-2 text-sm text-gray-700 md:col-span-2">
                    <input
                      type="checkbox"
                      {...register('validation_requise_deplacement')}
                      className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                    />
                    <span>Validation requise avant déplacement</span>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-3">
                  Les champs avancés (compétences, contraintes spécifiques, etc.) seront rendus
                  dynamiquement via le Form Builder une fois un schéma "service" configuré.
                </p>
              </form>
            )}
          </div>

          <div className="flex items-center justify-between p-4 border-t border-gray-200 flex-shrink-0">
            <div>
              {isEdit && (
                <button
                  type="button"
                  onClick={handleDelete}
                  className="text-sm text-red-600 hover:text-red-700 font-medium"
                  disabled={saving}
                >
                  Supprimer
                </button>
              )}
            </div>
            <div className="flex items-center space-x-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-gray-300 rounded-lg text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
              >
                Annuler
              </button>
              <button
                type="submit"
                form="service-form"
                disabled={saving || loading}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-500 hover:bg-primary-600 disabled:opacity-50"
              >
                {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {isEdit ? 'Enregistrer' : 'Créer'}
              </button>
            </div>
          </div>
        </div>
      </div>
    </ModalPortal>
  );
}

const inputClass =
  'w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500';

function Field({
  label,
  error,
  children,
  className,
}: {
  label: string;
  error?: string;
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className={className}>
      <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
      {children}
      {error && <p className="text-xs text-red-500 mt-1">{error}</p>}
    </div>
  );
}
